import React, { useEffect, useRef, useState } from "react";
import { MODELS } from "../../constants/models";
import ShareButton from "./ShareButton";
import { isObjectId } from "../../services/ChatHistoryService";
import ConfirmDialog from "../common/ConfirmDialog";
import ApiKeyModal from "./ApiKeyModal";
import { hasApiKey } from "../../services/ChatService";

function TopBar({
  onToggleSidebar,
  selectedModel,
  onModelChange,
  onClearHistory,
  userDisplayName,
  userEmail,
  onLogout,
  activeConversationId,
}) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [apiKeySet, setApiKeySet] = useState(hasApiKey());
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initial = (userDisplayName || userEmail || "U").charAt(0).toUpperCase();

  return (
    <header className="pim-header">
      <button
        onClick={onToggleSidebar}
        className="pim-header-icon-btn"
        title="Toggle sidebar"
        aria-label="Toggle sidebar"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      <div className="flex-1" />

      {activeConversationId && (
        <ShareButton
          conversationId={activeConversationId}
          isObjectIdFn={isObjectId}
        />
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={() => setApiKeyModalOpen(true)}
          className="pim-header-icon-btn relative"
          title={apiKeySet ? "API key đã lưu" : "Nhập API key"}
          aria-label="Cài đặt API key"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
            />
          </svg>
          <span
            className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${apiKeySet ? "bg-green-500" : "bg-red-500"
              }`}
          />
        </button>
        <span className="text-xs text-gray-500 hidden sm:inline">Model:</span>
        <select
          value={selectedModel}
          onChange={(e) => onModelChange(e.target.value)}
          className="pim-model-select"
          aria-label="Chọn model"
        >
          {MODELS.map((model) => (
            <option key={model.id} value={model.id}>
              {model.label}
            </option>
          ))}
        </select>
      </div>

      {/* User dropdown */}
      <div className="relative" ref={userMenuRef}>
        <button
          onClick={() => setIsUserMenuOpen((prev) => !prev)}
          className="pim-user-btn"
          aria-haspopup="menu"
          aria-expanded={isUserMenuOpen}
          title={userDisplayName || "Người dùng"}
        >
          <div className="pim-user-btn-avatar">{initial}</div>
          <div className="hidden sm:flex sm:flex-col sm:items-start leading-tight">
            <span className="text-[12.5px] font-semibold text-gray-800 max-w-32 truncate">
              {userDisplayName || "Người dùng"}
            </span>
            {userEmail && (
              <span className="text-[11px] text-gray-500 max-w-32 truncate">
                {userEmail}
              </span>
            )}
          </div>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isUserMenuOpen ? "rotate-180" : ""
              }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isUserMenuOpen && (
          <div
            className="absolute right-0 mt-2 w-64 rounded-xl border border-gray-200 bg-white shadow-lg z-50 overflow-hidden"
            style={{ animation: "pimSlideUp 0.15s ease-out" }}
            role="menu"
          >
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {userDisplayName || "Người dùng"}
              </p>
              {userEmail && (
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  {userEmail}
                </p>
              )}
            </div>
            <button
              onClick={() => {
                setIsUserMenuOpen(false);
                setApiKeyModalOpen(true);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              role="menuitem"
            >
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              API key
              <span
                className={`ml-auto text-[11px] ${apiKeySet ? "text-green-600" : "text-red-500"
                  }`}
              >
                {apiKeySet ? "Đã lưu" : "Chưa có"}
              </span>
            </button>
            {onClearHistory && (
              <button
                onClick={() => {
                  setIsUserMenuOpen(false);
                  setConfirmClearOpen(true);
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                role="menuitem"
              >
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.8}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Xóa toàn bộ lịch sử
              </button>
            )}
            <button
              onClick={() => {
                setIsUserMenuOpen(false);
                onLogout();
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
              role="menuitem"
            >
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h5a2 2 0 012 2v1"
                />
              </svg>
              Đăng xuất
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmClearOpen}
        title="Xoá toàn bộ lịch sử?"
        message="Toàn bộ cuộc trò chuyện của bạn sẽ bị xoá vĩnh viễn và không thể khôi phục."
        confirmText="Xoá tất cả"
        cancelText="Hủy"
        danger
        loading={clearing}
        onConfirm={async () => {
          if (!onClearHistory) return;
          setClearing(true);
          try {
            await onClearHistory();
            setConfirmClearOpen(false);
          } finally {
            setClearing(false);
          }
        }}
        onCancel={() => !clearing && setConfirmClearOpen(false)}
      />

      <ApiKeyModal
        open={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
        onSaved={() => setApiKeySet(hasApiKey())}
      />
    </header>
  );
}

export default TopBar;
